import { InvitationTarget } from "../__generated__/graphql";
import { InvitationFieldsFragment } from "../__generated__/operations/graphql";
import { useCallback, useMemo, useState } from "react";
import { ErrorLike } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { ACCEPT_INVITATION, CANCEL_INVITATION, REJECT_INVITATION } from "../graphql/invitation";
import { GET_CLIENT_USERS, GET_CLIENT_WORKERS_AND_INVITATION_WORKERS } from "../graphql/client";

export type InvitationAction = "close" | "accept" | "reject" | "cancel" | "resend";

type InvitationHookResult = {
    onAction: (
        action: InvitationAction,
        invitation: InvitationFieldsFragment,
    ) => void | Promise<void>;
    processingAction: InvitationAction | null;
    successAction: InvitationAction | null;
    error: ErrorLike | null;
    reset: () => void;
};

export const useInvitationHandler = ({
    onActionSuccess,
    onActionError,
    target,
}: {
    onActionSuccess?: (
        action: InvitationAction,
        invitation: InvitationFieldsFragment,
    ) => void | Promise<void>;
    onActionError?: (
        action: InvitationAction,
        invitation: InvitationFieldsFragment,
    ) => void | Promise<void>;
    target?: InvitationTarget | null;
}): InvitationHookResult => {
    const [processingAction, setProcessingAction] = useState<InvitationAction | null>(null);
    const [acceptInvitation, { error: acceptError, data: acceptData, reset: acceptReset }] =
        useMutation(ACCEPT_INVITATION);
    const [rejectInvitation, { error: rejectError, data: rejectData, reset: rejectReset }] =
        useMutation(REJECT_INVITATION);
    const [cancelInvitation, { error: cancelError, data: cancelData, reset: cancelReset }] =
        useMutation(CANCEL_INVITATION, {
            refetchQueries: [
                target === InvitationTarget.Worker
                    ? GET_CLIENT_WORKERS_AND_INVITATION_WORKERS
                    : GET_CLIENT_USERS,
            ],
        });

    //const processing = acceptLoading || rejectLoading || cancelLoading;
    const successAction = useMemo(() => {
        if (acceptData) {
            return "accept";
        }
        if (rejectData) {
            return "reject";
        }
        if (cancelData) {
            return "cancel";
        }
        return null;
    }, [acceptData, rejectData, cancelData]);
    const error = acceptError || rejectError || cancelError || null;
    const reset = useCallback(() => {
        setProcessingAction(null);
        acceptReset();
        rejectReset();
        cancelReset();
    }, [acceptReset, rejectReset, cancelReset]);

    const onAction = useCallback(
        async (action: InvitationAction, inv: InvitationFieldsFragment) => {
            reset();
            setProcessingAction(action);
            try {
                switch (action) {
                    case "accept":
                        await acceptInvitation({ variables: { token: inv.token } });
                        break;
                    case "reject":
                        await rejectInvitation({ variables: { token: inv.token } });
                        break;
                    case "cancel":
                        await cancelInvitation({ variables: { invitationId: inv.id } });
                        break;
                    default:
                        break;
                }
                // simulate a delay
                // await new Promise((resolve) => setTimeout(() => void resolve(null), 2000));
                if (onActionSuccess) await onActionSuccess(action, inv);
            } catch {
                await onActionError?.(action, inv);
            } finally {
                setProcessingAction(null);
            }
        },
        [
            reset,
            onActionSuccess,
            acceptInvitation,
            rejectInvitation,
            cancelInvitation,
            onActionError,
        ],
    );

    return {
        onAction,
        processingAction,
        successAction,
        error,
        reset,
    };
};
